import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyIcon from '@mui/icons-material/Key';
import ApiKeysService from '../../services/ApiKeysService';
import { errorMessage } from '../../shared/utils/validators';

function fmtDateTime(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(value);
  }
}

export default function ApiKeysSettings() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [confirmRevokeId, setConfirmRevokeId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await ApiKeysService.list();
      setKeys(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(errorMessage(err) || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleCreate(e) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setSnackbar({ open: true, message: 'Enter a name for this key.', severity: 'warning' });
      return;
    }
    setCreating(true);
    try {
      const created = await ApiKeysService.create(trimmed);
      setCreatedKey(created);
      setName('');
      setCopied(false);
      await load();
      setSnackbar({
        open: true,
        message: 'API key created. Copy it now — it won’t be shown again.',
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: errorMessage(err) || 'Failed to create key',
        severity: 'error',
      });
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id) {
    setRevokingId(id);
    try {
      await ApiKeysService.revoke(id);
      setConfirmRevokeId(null);
      await load();
      setSnackbar({ open: true, message: 'API key revoked.', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: errorMessage(err) || 'Failed to revoke key',
        severity: 'error',
      });
    } finally {
      setRevokingId(null);
    }
  }

  async function copyKey() {
    if (!createdKey?.key) return;
    try {
      await navigator.clipboard.writeText(createdKey.key);
      setCopied(true);
      setSnackbar({ open: true, message: 'Copied to clipboard.', severity: 'success' });
    } catch {
      // Fallback when Clipboard API is blocked (non-secure context / permissions)
      try {
        const el = document.createElement('textarea');
        el.value = createdKey.key;
        el.setAttribute('readonly', '');
        el.style.position = 'fixed';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        setCopied(true);
        setSnackbar({ open: true, message: 'Copied to clipboard.', severity: 'success' });
      } catch {
        setSnackbar({ open: true, message: 'Could not copy. Select and copy manually.', severity: 'error' });
      }
    }
  }

  const activeKeys = keys.filter((k) => !k.revokedAt);
  const revokedKeys = keys.filter((k) => k.revokedAt);

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        API keys
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Use a long-lived key as{' '}
        <Box component="code" sx={{ bgcolor: 'action.hover', px: 0.75, py: 0.25, borderRadius: 1 }}>
          Authorization: Bearer &lt;key&gt;
        </Box>{' '}
        on the WappGrow MCP connection. Prefer this over putting your password into the login tool.
      </Typography>

      {loading ? (
        <Typography variant="body2" color="text.secondary">Loading…</Typography>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      ) : (
        <>
          {createdKey?.key && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                New key — copy it now
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                This is the only time the full key is shown.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <Box
                  component="code"
                  sx={{
                    flex: 1,
                    wordBreak: 'break-all',
                    bgcolor: 'background.paper',
                    p: 1,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                  }}
                >
                  {createdKey.key}
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<ContentCopyIcon />}
                  onClick={copyKey}
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
                <Button size="small" onClick={() => setCreatedKey(null)}>Dismiss</Button>
              </Stack>
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleCreate}
            sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Create a key</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                size="small"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Cursor MCP"
                inputProps={{ maxLength: 255 }}
              />
              <Button type="submit" variant="contained" disabled={creating}>
                {creating ? 'Creating…' : 'Create'}
              </Button>
            </Stack>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Active keys</Typography>
          {activeKeys.length === 0 ? (
            <Box
              sx={{
                py: 4,
                px: 2,
                textAlign: 'center',
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'action.hover',
              }}
            >
              <KeyIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No API keys yet. Create one above for MCP clients.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1} sx={{ mb: 3 }}>
              {activeKeys.map((key) => (
                <Box
                  key={key.id}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography variant="body1" fontWeight={600}>{key.name}</Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                      {key.keyPrefix}…
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Created {fmtDateTime(key.createdAt)}
                      {key.lastUsedAt ? ` · Last used ${fmtDateTime(key.lastUsedAt)}` : ' · Never used'}
                    </Typography>
                  </Box>
                  {confirmRevokeId === key.id ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption">Revoke?</Typography>
                      <Button
                        size="small"
                        color="error"
                        variant="contained"
                        disabled={revokingId === key.id}
                        onClick={() => handleRevoke(key.id)}
                      >
                        Yes
                      </Button>
                      <Button size="small" onClick={() => setConfirmRevokeId(null)}>Cancel</Button>
                    </Stack>
                  ) : (
                    <IconButton
                      color="error"
                      aria-label="Revoke key"
                      onClick={() => setConfirmRevokeId(key.id)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Stack>
          )}

          {revokedKeys.length > 0 && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Revoked</Typography>
              <Stack spacing={1}>
                {revokedKeys.map((key) => (
                  <Box key={key.id} sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', opacity: 0.75 }}>
                    <Typography variant="body2">{key.name}</Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {key.keyPrefix}…
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Revoked {fmtDateTime(key.revokedAt)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
